import type { FraudParameters, InitFraudNet } from './types';

/**
 * fraudNet
 *
 * initializes FraudNet v2.1 for client app by writing to document body
 * with your app's set configuration
 *
 * @param userToken the EC/PAY/BA token of the end user
 * @param sourceId the unique source Id of your app to be registered
 * for lite apps we recommend a namespace fo CHECKOUTUINODEWEB_<YOUR_APP_NAME>
 * i.e. CHECKOUTUINODEWEB_MEMBER_LITE
 * @param endpoint the endpoint that your app will post FraudNet payloads to
 * @param typingSpeed Enables and configures telemetry for input fields
 */
export const fraudNet = ({
  addCrossOrigin = true,
  endpoint,
  sourceId,
  typingSpeed,
  userToken,
}: InitFraudNet) => {
  /**
   * There have been occurrences of the `removeChild()` call throwing:
   *    NotFoundError: Failed to execute 'removeChild' on 'Node': The
   *    node to be removed is not a child of this node.
   */
  const existingConfig = document.getElementById('fconfig');
  if (existingConfig) {
    try {
      // reset fraudnet if somehow initialized beforehand
      if (existingConfig.parentElement) {
        existingConfig.parentElement.removeChild(existingConfig);
      }
    } catch (e) {
      // Change the id so that fraudnet won't use this config. The config should
      // be static on a token basis but if an error is being thrown by trying to
      // remove the element we'll update the reset the config just to be safe.
      existingConfig.id = 'stale_fconfig';
    }
  }

  // See `Configuration parameters` section
  // https://engineering.paypalcorp.com/confluence/display/RiskDataAcquisition/FraudNet+v2.x+SDK+Developer+Guide
  const configuration: FraudParameters = {
    f: userToken,
    s: sourceId,
  };

  if (typingSpeed) {
    configuration.ts = typingSpeed;
  }

  // this HAS to be done before loading fraudnet as fraudnet relies on the
  // fconfig block to properly intialize
  const fconfig: HTMLScriptElement = document.createElement('script');
  fconfig.id = 'fconfig';
  fconfig.type = 'application/json';
  // these values are provided by the FraudNet team and hard coded purposely
  fconfig.setAttribute('fncls', 'fnparams-dede7cc5-15fd-4c75-a9f4-36c430ee3a99');
  fconfig.text = JSON.stringify(configuration);
  document.body.appendChild(fconfig);

  const fraudnet: HTMLScriptElement = document.createElement('script');
  fraudnet.async = true; // async load fraudnet lib
  fraudnet.src = endpoint;

  // CORS headers are present only in production
  // so on localhost / stage script won't be loaded with "crossOrigin" attribute
  if (addCrossOrigin) {
    fraudnet.crossOrigin = 'anonymous';
  }

  document.body.appendChild(fraudnet);
};
